import { useState, useMemo, SetStateAction, Dispatch } from 'react';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useAuth from '@sdk/authentication/useAuth';

import {
  ShiftTaskDashboardNotification,
  ShiftTaskDashboardNotificationItem,
} from '@web/shiftTasks/types/ShiftTaskDashboardNotification';
import { useGetShiftTaskDetails } from '@web/shiftTasks/hooks/useGetShiftTaskDetails';
import { useCompleteShiftTask } from '@web/shiftTasks/hooks/useCompleteTask';
import { TaskStatuses } from '@web/shiftTasks/types/TaskStatuses';

type Props = {
  notification: ShiftTaskDashboardNotification;
  onClose: VoidFunction;
};

export const useShiftTaskNotification = ({ notification, onClose }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentNotificationItem = notification.items[currentIndex];
  const { data: shiftTask, isLoading } = useGetShiftTaskDetails(
    currentNotificationItem.id,
    onClose
  );

  const headerProps = useMemo(
    () =>
      getHeaderProps(
        currentNotificationItem,
        notification.items.length,
        currentIndex,
        setCurrentIndex
      ),
    [currentIndex, currentNotificationItem, notification.items]
  );

  return {
    currentNotificationItem,
    isLoading,
    shiftTask,
    headerProps,
  };
};

type NotificationActionsProps = {
  id: ID;
  status: TaskStatuses;
  assignee: {
    id: ID;
  };
};

export const useShiftTaskNotificationActions = (shiftTask: NotificationActionsProps | null) => {
  const completeTaskMutation = useCompleteShiftTask();
  const { showSuccessToast, showErrorToast } = useToast();
  const { currentUser } = useAuth();

  const canCompleteTask =
    shiftTask?.status === TaskStatuses.PENDING &&
    (currentUser.isManager || shiftTask.assignee.id === currentUser.id);

  const completeTask = async () => {
    try {
      await completeTaskMutation.mutateAsync(shiftTask?.id);
      showSuccessToast('Task successfully completed.');
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    }
  };

  return { canCompleteTask, completeTask };
};

function getHeaderProps(
  currentNotificationItem: ShiftTaskDashboardNotificationItem,
  itemsCount: number,
  currentIndex: number,
  setCurrentIndex: Dispatch<SetStateAction<number>>
) {
  const goToNextItem = () => {
    setCurrentIndex((index) => Math.min(index + 1, itemsCount));
  };

  const goToPreviousItem = () => {
    setCurrentIndex((index) => Math.max(index - 1, 0));
  };

  return {
    currentIndex,
    goToNextItem,
    goToPreviousItem,
    itemsCount,
    title: `[${currentNotificationItem.title}]`,
  };
}
